import { motion } from "framer-motion";
import {
  Briefcase,
  FolderGit2,
  Mic,
  Users,
  Cpu,
  Award,
  Rocket,
  Sparkles,
} from "lucide-react";

const promises = [
  {
    Icon: Sparkles,
    title: "AI-Integrated Learning",
    desc: "Master your subjects using industry-grade tools like Claude, Cursor, and GitHub Copilot. AI isn't an afterthought here it’s the foundation of how you build.",
    grad: "from-blue-500 to-cyan-400",
  },
  {
    Icon: Briefcase,
    title: "Guaranteed Internships",
    desc: "Secure critical real-world experience during your 4 years. Tap directly into elite opportunities powered by AI.Prof's massive 1,800+ hiring company network.",
    grad: "from-orange-500 to-amber-400",
  },
  {
    Icon: FolderGit2,
    title: "Production-Ready Projects",
    desc: "Ditch exam-only courses. Every single subject concludes with a live, deployment-ready project to stack your portfolio and prove your capabilities to employers.",
    grad: "from-fuchsia-500 to-purple-500",
  },
  {
    Icon: Mic,
    title: "Elite Interview Preparation",
    desc: "Dominate the selection process with dedicated modules every semester covering hard-hitting technical talks, ruthless mock HR interviews, and salary negotiation tactics.",
    grad: "from-emerald-500 to-teal-400",
  },
  {
    Icon: Users,
    title: "Silicon Valley Mentorship",
    desc: "Learn from the insiders. Gain direct access to active tech leads and architects working in AI/ML, data science, and cybersecurity not just standard academic faculty.",
    grad: "from-rose-500 to-pink-500",
  },
  {
    Icon: Cpu,
    title: "Veda AI — 24/7 Co-Pilot",
    desc: "Never get stuck on a line of code again. Your always-on AI mentor reviews your repositories, debugs your logic, and refines your learning round the clock.",
    grad: "from-indigo-500 to-blue-500",
  },
  {
    Icon: Award,
    title: "Verified Skill Portfolio",
    desc: "Graduate with undeniable proof. Replace empty certificates with a verified digital portfolio of live projects that automatically maps your talent directly to top employer demands.",
    grad: "from-amber-500 to-yellow-400",
  },
  {
    Icon: Rocket,
    title: "1,800+ Hiring Partners",
    desc: "Gain a direct pipeline to elite tech giants including Microsoft, Google, AWS, IBM, and Qualcomm a massive network actively looking to recruit our verified talent.",
    grad: "from-aiorange to-red-500",
  },
];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};

const item = {
  hidden: { opacity: 0, y: 32, scale: 0.96 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: "spring", stiffness: 120, damping: 16 },
  },
};

export default function SmruPromises() {
  return (
    <section
      id="features"
      className="relative overflow-hidden py-24 sm:py-32 bg-gradient-to-br from-[#050B2E] via-[#0A2665] to-[#1b0f40]"
    >
      {/* Animated vibrant background blobs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="float-slow absolute -top-32 -left-24 w-[34rem] h-[34rem] rounded-full bg-aiblue/30 blur-[120px]" />
        <div className="float-fast absolute top-1/3 -right-32 w-[32rem] h-[32rem] rounded-full bg-aiorange/25 blur-[120px]" />
        <div className="float-slow absolute -bottom-40 left-1/3 w-[30rem] h-[30rem] rounded-full bg-fuchsia-500/20 blur-[120px]" />
        {/* faint dotted grid */}
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "radial-gradient(rgba(255,255,255,0.8) 1px, transparent 1px)",
            backgroundSize: "26px 26px",
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-white/10 ring-1 ring-white/15 text-amber-300 backdrop-blur-sm">
            <Sparkles className="h-3.5 w-3.5" />
            Our Promise To You
          </span>
          <h2 className="mt-5 font-display text-4xl sm:text-5xl font-black tracking-tight text-white">
            Everything Built Into Your{" "}
            <span className="bg-gradient-to-r from-[#5B9BFF] to-[#FF8A3D] bg-clip-text text-transparent">
              4 Years
            </span>
          </h2>
          <p className="mt-4 text-blue-100/70 text-lg">
            <span className="font-bold text-white">AI.Prof × SMRU</span>{" "}
            commitments that turn a degree into a career.
          </p>
        </motion.div>

        {/* Cards */}
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5"
        >
          {promises.map(({ Icon, title, desc, grad }) => (
            <motion.div
              key={title}
              variants={item}
              whileHover={{ y: -8 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              className="group relative rounded-2xl p-6 bg-white/[0.06] ring-1 ring-white/10 backdrop-blur-sm hover:bg-white/[0.1] hover:ring-white/25 transition-colors duration-300 overflow-hidden"
            >
              {/* hover glow wash in the card's accent colour */}
              <div
                className={`pointer-events-none absolute -top-16 -right-16 w-40 h-40 rounded-full bg-gradient-to-br ${grad} opacity-0 group-hover:opacity-30 blur-2xl transition-opacity duration-500`}
              />

              {/* Icon badge */}
              <div
                className={`relative w-14 h-14 rounded-2xl bg-gradient-to-br ${grad} flex items-center justify-center shadow-lg shadow-black/30 transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-6`}
              >
                <Icon size={26} className="text-white" strokeWidth={2} />
              </div>

              <h3 className="relative mt-5 font-display font-bold text-lg text-white leading-snug">
                {title}
              </h3>
              <p className="relative mt-2 text-sm text-blue-100/70 leading-relaxed">
                {desc}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
